import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { SectionScaffold } from '../../components/SectionScaffold/SectionScaffold'
import { ProtectedImage } from '../../components/ProtectedImage/ProtectedImage'
import { assets } from '../../data/assets'
import { content } from '../../data/content'
import { useWechatMediaMode } from '../../hooks/useWechatMediaMode'
import {
  createMotionMedia,
  gsap,
  motionConditions,
  motionTokens,
  type MotionConditionState,
} from '../../motion'
import styles from './HeroSection.module.css'

const heroPoster = '/assets/optimized/posters/hero-1920.webp'
const heroVideoMobile = '/assets/optimized/videos/hero-mobile-1080.mp4'
const heroWechatAnimation = '/assets/animations/sec-001-wechat.webp'

export function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const videoLayerRef = useRef<HTMLDivElement>(null)
  const backgroundMediaRef = useRef<HTMLElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const supportRef = useRef<HTMLDivElement>(null)
  const [isWechatAnimationReady, setIsWechatAnimationReady] = useState(false)
  const { isWechat, reduceMotion } = useWechatMediaMode()
  const copy = content['SEC-001']
  const asset = assets['AST-001']
  const primaryTitleEnd = copy.title.indexOf('》') + 1
  const primaryTitle = copy.title.slice(0, primaryTitleEnd)
  const projectTitle = copy.title.slice(primaryTitleEnd)
  const [subtitleLineOne, subtitleLineTwo] = copy.subtitle.split('，')

  const bindVideo = useCallback((video: HTMLVideoElement | null) => {
    videoRef.current = video
    backgroundMediaRef.current = video

    if (!video) return

    video.autoplay = true
    video.defaultMuted = true
    video.muted = true
    video.loop = true
    video.playsInline = true
    video.setAttribute('autoplay', '')
    video.setAttribute('muted', '')
    video.setAttribute('loop', '')
    video.setAttribute('playsinline', '')
    video.setAttribute('webkit-playsinline', '')
  }, [])

  const bindWechatBackground = useCallback(
    (image: HTMLElement | null) => {
      backgroundMediaRef.current = image
    },
    [],
  )

  useEffect(() => {
    const video = videoRef.current

    if (!video) return

    let disposed = false

    const playVideo = () => {
      if (disposed || !video.paused) return
      video.defaultMuted = true
      video.muted = true
      void video.play().catch(() => undefined)
    }

    const restartVideo = () => {
      if (disposed) return
      video.currentTime = 0
      playVideo()
    }

    const resumeVisibleVideo = () => {
      if (document.visibilityState === 'visible') playVideo()
    }

    video.addEventListener('canplay', playVideo)
    video.addEventListener('loadeddata', playVideo)
    video.addEventListener('ended', restartVideo)
    window.addEventListener('pageshow', playVideo)
    document.addEventListener('visibilitychange', resumeVisibleVideo)
    document.addEventListener('touchstart', playVideo, { passive: true })
    document.addEventListener('pointerdown', playVideo, { passive: true })
    document.addEventListener('WeixinJSBridgeReady', playVideo)
    playVideo()

    return () => {
      disposed = true
      video.removeEventListener('canplay', playVideo)
      video.removeEventListener('loadeddata', playVideo)
      video.removeEventListener('ended', restartVideo)
      window.removeEventListener('pageshow', playVideo)
      document.removeEventListener('visibilitychange', resumeVisibleVideo)
      document.removeEventListener('touchstart', playVideo)
      document.removeEventListener('pointerdown', playVideo)
      document.removeEventListener('WeixinJSBridgeReady', playVideo)
    }
  }, [])

  useLayoutEffect(() => {
    const section = sectionRef.current
    const videoLayer = videoLayerRef.current
    const backgroundMedia = backgroundMediaRef.current
    const title = titleRef.current
    const support = supportRef.current

    if (!section || !videoLayer || !backgroundMedia || !title || !support) return

    const media = createMotionMedia()

    media.add(motionConditions, (context) => {
      const { reduceMotion } = context.conditions as MotionConditionState

      if (reduceMotion) {
        gsap.set([backgroundMedia, videoLayer, title, support], {
          clearProps: 'all',
          opacity: 1,
          yPercent: 0,
        })
        return
      }

      gsap
        .timeline()
        .fromTo(
          backgroundMedia,
          { scale: motionTokens.scale.cinematic },
          {
            scale: 1,
            duration: motionTokens.duration.slow,
            ease: motionTokens.ease.entrance,
          },
        )
        .fromTo(
          title,
          { autoAlpha: 0, yPercent: 105 },
          {
            autoAlpha: 1,
            yPercent: 0,
            duration: motionTokens.duration.slow,
            ease: motionTokens.ease.entrance,
          },
          0.15,
        )
        .fromTo(
          support,
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration: motionTokens.duration.normal,
            ease: motionTokens.ease.standard,
          },
          0.55,
        )

      gsap.fromTo(
        videoLayer,
        {
          opacity: 1,
          filter: 'blur(0px)',
        },
        {
          opacity: 0,
          filter: 'blur(10px)',
          ease: 'none',
          immediateRender: false,
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
            invalidateOnRefresh: true,
          },
        },
      )
    })

    return () => media.revert()
  }, [])

  return (
    <SectionScaffold
      id="SEC-001"
      className={styles.section}
      labelledBy="sec-001-title"
    >
      <div ref={sectionRef} className={styles.viewport}>
        <div ref={videoLayerRef} className={styles.videoLayer}>
          {isWechat ? (
            <>
              <ProtectedImage
                ref={reduceMotion ? bindWechatBackground : undefined}
                className={styles.mediaPoster}
                src={heroPoster}
                alt=""
                aria-hidden="true"
                decoding="async"
                fetchPriority="high"
                draggable={false}
              />
              {!reduceMotion ? (
                <ProtectedImage
                  ref={bindWechatBackground}
                  className={`${styles.wechatAnimation} ${
                    isWechatAnimationReady ? styles.mediaReady : ''
                  }`}
                  src={heroWechatAnimation}
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  fetchPriority="high"
                  draggable={false}
                  onLoad={() => setIsWechatAnimationReady(true)}
                  onError={() => setIsWechatAnimationReady(false)}
                />
              ) : null}
            </>
          ) : (
            <video
              ref={bindVideo}
              className={styles.video}
              aria-label={asset.alt}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              poster={heroPoster}
              draggable={false}
            >
              <source
                media="(max-width: 47.9375rem)"
                src={heroVideoMobile}
                type="video/mp4"
              />
              <source src={asset.path} type="video/mp4" />
            </video>
          )}
        </div>
        <div className={styles.overlay} aria-hidden="true" />

        <div className={styles.content}>
          <div className={styles.titleMask}>
            <h1 ref={titleRef} id="sec-001-title" className={styles.title}>
              <span className={styles.primaryTitle}>{primaryTitle}</span>
              <span className={styles.projectTitle}>{projectTitle}</span>
            </h1>
          </div>
          <div ref={supportRef} className={styles.support}>
            <p className={styles.subtitle}>
              <span className={styles.subtitleDesktop}>{copy.subtitle}</span>
              <span className={styles.subtitleMobile}>
                <span>{subtitleLineOne}</span>
                <span>{subtitleLineTwo}</span>
              </span>
            </p>
            <p className={styles.body}>{copy.body}</p>
            <div className={styles.scrollIndicator} aria-hidden="true">
              <span>{copy.scrollLabel}</span>
              <span className={styles.scrollLine} />
            </div>
          </div>
        </div>
      </div>
    </SectionScaffold>
  )
}
